import { useTranslation } from "@sagot/i18n";
import { AppLink, Avatar, Card, CardHeader, CardTitle, Chip, EmptyState, Progress, Skeleton, buttonClasses, type ChipProps } from "@sagot/ui";
import { PRESENCE_META, statusLabelKey } from "../inbox/presence-model";
import { ReassignMenu, type ReassignChat } from "./reassign-menu";
import {
  formatClock,
  isAtLimit,
  loadPercent,
  reassignTargets,
  type ReassignTarget,
  type WallboardAgent,
} from "./wallboard-model";

const STATUS_TONE: Record<WallboardAgent["status"], NonNullable<ChipProps["tone"]>> = {
  online: "success",
  busy: "warning",
  away: "muted",
  offline: "muted",
};

/** Grid shared by the header and the rows; on a phone each row stacks (name and action, status and longest chat, then the load bar). */
const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 md:grid-cols-[minmax(0,1.6fr)_8.5rem_minmax(0,1.6fr)_6rem_7.5rem]";

interface AgentBoardProps {
  agents: readonly WallboardAgent[];
  canAssign: boolean;
  pending: boolean;
  onReassign: (chat: ReassignChat, target: ReassignTarget) => void;
  teamsHref: string;
}

function AgentRow({ agent, targets, canAssign, pending, onReassign }: { agent: WallboardAgent; targets: readonly ReassignTarget[]; canAssign: boolean; pending: boolean; onReassign: AgentBoardProps["onReassign"] }) {
  const { t } = useTranslation();
  const full = isAtLimit(agent);
  const chats: ReassignChat[] = agent.chats.map((chat) => ({ id: chat.conversation_id, title: chat.title, heldS: chat.held_s }));
  const loadText = t("wallboard.board.agents.loadText", { active: agent.active_chats, limit: agent.chat_limit });
  return (
    <div role="row" className={`${ROW_GRID} border-b border-line px-4 py-3 last:border-b-0`} data-testid={`agent-row-${agent.member_id}`} data-status={agent.status}>
      <div role="cell" className="order-1 flex min-w-0 items-center gap-2.5 md:order-none">
        <Avatar name={agent.name} className="size-8 shrink-0 text-xs" />
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-fg">{agent.name}</p>
          {agent.teams.length > 0 ? <p className="hidden truncate text-xs text-fg-muted md:block">{agent.teams.join(", ")}</p> : null}
        </div>
      </div>
      <div role="cell" className="order-3 md:order-none">
        <Chip tone={STATUS_TONE[agent.status]} icon={PRESENCE_META[agent.status].icon} data-testid={`agent-status-${agent.member_id}`}>
          {agent.auto_away ? t("wallboard.board.status.autoAway") : t(statusLabelKey(agent.status, false))}
        </Chip>
      </div>
      <div role="cell" className="order-5 col-span-2 min-w-0 md:order-none md:col-span-1">
        <div className="flex items-center gap-3">
          <Progress
            value={agent.active_chats}
            max={Math.max(1, agent.chat_limit)}
            label={t("wallboard.board.agents.loadLabel", { name: agent.name, active: agent.active_chats, limit: agent.chat_limit })}
            tone={full ? "warning" : "primary"}
            className="min-w-0 flex-1"
          />
          <span className="shrink-0 text-xs font-semibold text-fg-muted tabular-nums" data-testid={`agent-load-${agent.member_id}`} data-percent={loadPercent(agent.active_chats, agent.chat_limit)}>
            {loadText}
          </span>
        </div>
        {full ? <p className="mt-1 text-[11px] font-bold text-warning">{t("wallboard.board.agents.atLimit")}</p> : null}
      </div>
      <div role="cell" className="order-4 text-right text-sm font-semibold text-fg tabular-nums md:order-none" data-testid={`agent-longest-${agent.member_id}`}>
        {agent.longest_chat_s === null ? <span aria-label={t("wallboard.board.agents.none")}>–</span> : formatClock(agent.longest_chat_s)}
      </div>
      <div role="cell" className="order-2 flex justify-end md:order-none">
        {canAssign && agent.active_chats > 0 ? (
          <ReassignMenu
            chats={chats}
            targets={targets.filter((target) => target.memberId !== agent.member_id)}
            label={t("wallboard.board.reassign.button")}
            ariaLabel={t("wallboard.board.reassign.buttonLabel", { name: agent.name })}
            icon="users"
            pending={pending}
            holder={agent.name}
            onPick={onReassign}
            testId={`reassign-${agent.member_id}`}
          />
        ) : null}
      </div>
    </div>
  );
}

export function AgentBoardSkeleton() {
  return (
    <div className="space-y-3 p-4" data-testid="wallboard-agents-loading">
      {[0, 1, 2, 3].map((row) => <Skeleton key={row} className="h-12 w-full" />)}
    </div>
  );
}

/** The agent status board (mockup pin 2): status, load against the limit, longest chat and one-click reassign. */
export function AgentBoard({ agents, canAssign, pending, onReassign, teamsHref }: AgentBoardProps) {
  const { t } = useTranslation();
  const targets = reassignTargets(agents, null);
  return (
    <Card data-testid="wallboard-agents">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{t("wallboard.board.agents.title")}</CardTitle>
          <p className="mt-0.5 text-xs text-fg-muted">{t("wallboard.board.agents.subtitle")}</p>
        </div>
      </CardHeader>
      {agents.length === 0 ? (
        <EmptyState
          icon="users"
          title={t("wallboard.board.agents.emptyTitle")}
          description={t("wallboard.board.agents.emptyBody")}
          headingLevel={3}
          action={<AppLink href={teamsHref} className={buttonClasses({ variant: "secondary", size: "sm" })}>{t("wallboard.board.agents.emptyAction")}</AppLink>}
        />
      ) : (
        <div role="table" aria-label={t("wallboard.board.agents.tableLabel")}>
          <div role="rowgroup" className="hidden md:block">
            <div role="row" className={`${ROW_GRID} border-b border-line bg-surface-2 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-fg-subtle`}>
              <div role="columnheader">{t("wallboard.board.agents.agent")}</div>
              <div role="columnheader">{t("wallboard.board.agents.status")}</div>
              <div role="columnheader">{t("wallboard.board.agents.load")}</div>
              <div role="columnheader" className="text-right">{t("wallboard.board.agents.longest")}</div>
              <div role="columnheader" className="text-right"><span className="sr-only">{t("wallboard.board.agents.actions")}</span></div>
            </div>
          </div>
          <div role="rowgroup">
            {agents.map((agent) => (
              <AgentRow key={agent.member_id} agent={agent} targets={targets} canAssign={canAssign} pending={pending} onReassign={onReassign} />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
